import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useCan } from "../../auth/AuthProvider";
import { Icon } from "../../components/icons";
import { RuntimePanel } from "../../components/RuntimePanel";
import { ScalingCard } from "../../components/ScalingCard";
import {
  Button,
  Card,
  CardHeader,
  DeploymentBadge,
  EmptyState,
  LoadingBlock,
  Mono,
} from "../../components/ui";
import { absoluteTime, duration } from "../../lib/format";
import { useApp, useAppRuntime, useDeployments, useDomains, useRestartApp } from "../../lib/queries";
import { useAppTabContext } from "./context";

/*
 * The source, build type, port and plan used to be a "Configuration" card in
 * this sidebar. They are in the page header now — they are facts you glance at
 * once, and giving them a panel gave them the same weight as the runtime
 * state, which changes minute to minute and is the reason anyone opens this
 * page. What is left here is the two things that move.
 */
export function OverviewTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  /** Developer, which is what both the restart and the replica count need. */
  const canEdit = useCan("developer");
  const { data: app } = useApp(orgId, appId);
  const { data: history, isPending } = useDeployments(orgId, appId, 1);
  const { data: domains } = useDomains(orgId, appId);
  const runtime = useAppRuntime(orgId, appId);
  const restart = useRestartApp(orgId ?? "", appId ?? "");

  const latest = history?.data[0];

  return (
    <div className="space-y-6">
      {/*
        Full width and first. It answers "is this thing healthy", which is why
        anyone opens the page, and it now carries six readings — CPU, memory and
        four I/O counters — which do not fit a sidebar column.
      */}
      <Card>
        <CardHeader
          title={t("overview.runtime")}
          action={
            canEdit ? (
              <Button
                variant="secondary"
                size="sm"
                icon="refresh"
                loading={restart.isPending}
                onClick={() => restart.mutate()}
              >
                {t("overview.restart")}
              </Button>
            ) : undefined
          }
        />
        <div className="p-5">
          <RuntimePanel
            report={runtime.data}
            loading={runtime.isLoading}
            error={runtime.error}
            onRetry={() => void runtime.refetch()}
            cpuLimit={app?.plan?.resources.cpu}
          />
        </div>
      </Card>

      {/*
        Under the runtime and above everything else, because the two answer
        one question in sequence: how is it doing, and how many of it are
        there. It lived on Settings until the tab had five cards and this one
        sat between the source and Delete -- a control somebody uses on a
        Tuesday afternoon, filed with the ones they use once.
      */}
      {app && orgId && <ScalingCard orgId={orgId} app={app} canEdit={canEdit} />}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
        <CardHeader
          title={t("overview.latest")}
          action={
            latest ? (
              <Link
                to="deployments"
                className="text-sm text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              >
                {t("allDeployments")}
              </Link>
            ) : undefined
          }
        />
        {isPending ? (
          <LoadingBlock />
        ) : !latest ? (
          <EmptyState
            title={t("overview.empty")}
            description={t("overview.emptyDescription")}
          />
        ) : (
          <div className="space-y-5 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <DeploymentBadge status={latest.status} />
              <span className="text-sm text-fg">{latest.title}</span>
            </div>

            {/*
              A push carries the commit that caused it, which is the thing
              someone scanning for "what shipped" is looking for. The old
              version showed the title and the trigger and left the commit to
              the deployments tab.
            */}
            {latest.commit && (
              <div className="flex items-start gap-2.5 border-l-2 border-line pl-3 text-sm">
                <Icon name="branch" className="mt-1 size-3.5 shrink-0 text-fg-subtle" />
                <div className="min-w-0">
                  <Mono className="text-fg">{latest.commit.shortSha}</Mono>
                  {latest.commit.message && (
                    <span className="ml-2 text-fg-muted">{latest.commit.message}</span>
                  )}
                </div>
              </div>
            )}

            <dl className="grid gap-4 sm:grid-cols-3">
              <Detail label={t("overview.started")}>
                {absoluteTime(latest.startedAt ?? latest.createdAt)}
              </Detail>
              <Detail label={t("overview.duration")}>
                {duration(latest.startedAt, latest.finishedAt)}
              </Detail>
              <Detail label={t("overview.triggeredBy")}>{latest.triggeredBy ?? "—"}</Detail>
            </dl>

            {latest.errorMessage && (
              <p className="border-l-2 border-l-danger pl-3 text-sm text-fg-muted">
                {latest.errorMessage}
              </p>
            )}
          </div>
        )}
      </Card>

        <div className="space-y-6">
          <Card>
          <CardHeader
            title={t("overview.domains")}
            action={
              domains && domains.length > 0 ? (
                <Link
                  to="domains"
                  className="text-sm text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                >
                  {t("overview.manage")}
                </Link>
              ) : undefined
            }
          />
          {!domains || domains.length === 0 ? (
            // "No domains attached yet" is a fact, not an instruction. An app
            // with no domain cannot be reached at all, which is the single most
            // likely reason someone is looking at this page confused.
            <div className="px-5 py-6">
              <p className="text-sm text-fg-muted">
                {t("overview.noAddress")}
              </p>
              {/*
                `to="domains"`, and every link on this tab is bare for the same
                reason. This is the *index* route of `apps/:appId`, so its URL is
                `/apps/:appId` with no segment of its own — a `../domains` here
                walks off the app id rather than off a tab name and resolves to
                `/apps/domains`, which still matches `apps/:appId` and opens the
                detail page for an app called "domains". All three links on this
                tab shipped that way. `test/app-overview-links.test.tsx` renders
                them and reads the hrefs back, because the rule is unintuitive
                enough that reading the JSX will not tell you.
              */}
              <Link
                to="domains"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              >
                {t("overview.addDomain")}
                <Icon name="chevron" className="size-3.5" />
              </Link>
            </div>
          ) : (
            <ul className="space-y-2 p-5">
              {domains.map((domain) => (
                <li key={domain.id}>
                  <a
                    href={domain.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="break-all font-mono text-sm text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                  >
                    {domain.host}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Card>

          {app?.description && (
            <Card>
              <CardHeader title={t("overview.description")} />
              <p className="p-5 text-sm leading-6 text-fg-muted">{app.description}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="micro">{label}</dt>
      <dd className="mt-1.5 text-sm text-fg">{children}</dd>
    </div>
  );
}
